// Kojiru — UTM capture and cross-page persistence.
//
// On any page load: if the URL carries utm_* params, freeze them into
// localStorage with a 30-day TTL. Any form on the site can then call
// getUTMs() to include the attribution in its payload — regardless of
// whether the visitor entered on the ad-linked page or navigated in from
// another Kojiru page or from docs.kojiru.com.
//
// Cross-subdomain propagation (docs.kojiru.com → kojiru.com) is handled
// by docs/docs/js/utm.js which rewrites outbound links; on landing back
// on kojiru.com the params are captured here and the loop closes.

const STORAGE_KEY = "kojiru_utm";
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

function readFromUrl() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const utm = {};
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) utm[k] = v;
  });
  return Object.keys(utm).length ? utm : null;
}

function readFromStorage() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.expires_at !== "number" || Date.now() > parsed.expires_at) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.utm || null;
  } catch {
    return null;
  }
}

function writeToStorage(utm) {
  if (typeof window === "undefined" || !utm) return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        utm,
        captured_at: Date.now(),
        expires_at: Date.now() + TTL_MS,
      }),
    );
  } catch {
    /* localStorage may be blocked (Safari private mode); silently no-op */
  }
}

/**
 * Call once on app boot. Freezes any utm_* URL params into localStorage.
 * Idempotent — later ad-clicks overwrite the stored value with fresher params.
 */
export function captureUTMs() {
  const fromUrl = readFromUrl();
  if (fromUrl) writeToStorage(fromUrl);
}

/**
 * Return the current UTM object (URL params first, then storage fallback).
 * Returns `null` if no attribution is available.
 */
export function getUTMs() {
  return readFromUrl() || readFromStorage();
}

/** Clear stored UTMs (called after a successful signup attribution). */
export function clearUTMs() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}
